/**
 * The console's half of the two emailed links that act on a person's own address — plan-014 GAP (the MAIL hand-off;
 * FR12-SEC-01, owner answer 41): `/verify-email#<token>` posts to `verifyPersonEmail`
 * (`POST /v1/auth/email-verification`, apps/api/src/routes/auth-session) and `/me/security/email-change#<token>` posts
 * to C1's `confirmMyEmailChange` (`POST /v1/me/email-changes/confirm`, apps/api/src/routes/personal-security).
 *
 * OPTIONAL ON THE GATEWAY, like the operator-action confirmation (`../confirm-operator-action/…-gateway.ts`): the
 * methods are merged onto `ConsoleGateway` here and the API binding below is spread into `apiGateway`. The sandbox
 * sends no email, so it carries neither, and each page says plainly that there is nothing to confirm here.
 */
import { ApiError, assertOk, type WeTalkApiClient } from "@wetalk/api-client";

/** The outcome of an email change: the address the person now signs in with. */
export interface EmailChangeConfirmedView {
  readonly email: string;
}

declare module "../../api/gateway.js" {
  interface ConsoleGateway {
    /** Verifies the person's address from the link's token. `404 not_found` for a used, expired or unknown link. */
    readonly verifyEmailAddress?: (token: string) => Promise<void>;
    /** Confirms an email change from the link's token. `404 not_found` as above; `409 email_taken` when another sign-in took it. */
    readonly confirmEmailChange?: (token: string) => Promise<EmailChangeConfirmedView>;
    /**
     * plan-014 review I-17: sends the signed-in person a new verification link (the old one stops working) —
     * `sent`, or `already_verified` (nothing sent); `429 rate_limited` past the person's window, `404 not_found` while
     * WeTalk sends no email.
     */
    readonly resendEmailVerification?: () => Promise<"sent" | "already_verified">;
  }
}

function recordOf(value: unknown, what: string): Readonly<Record<string, unknown>> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new ApiError(500, { error: { code: "internal_error", message: `${what} is not an object` } }, null);
  }
  return value as Readonly<Record<string, unknown>>;
}

function dataOf(answered: Parameters<typeof assertOk>[0], what: string): Readonly<Record<string, unknown>> {
  return recordOf(recordOf(assertOk(answered), what)["data"], `${what}.data`);
}

/** The API binding, spread into `apiGateway` beside the other slices. */
export function mailLandingApiSlice(client: WeTalkApiClient): {
  readonly verifyEmailAddress: (token: string) => Promise<void>;
  readonly confirmEmailChange: (token: string) => Promise<EmailChangeConfirmedView>;
  readonly resendEmailVerification: () => Promise<"sent" | "already_verified">;
} {
  return {
    async resendEmailVerification() {
      const data = dataOf(await client.resendPersonEmailVerification({}), "resendPersonEmailVerification");
      const state = data["state"];
      if (state !== "sent" && state !== "already_verified") {
        throw new ApiError(500, { error: { code: "internal_error", message: "resendPersonEmailVerification answered an unknown state" } }, null);
      }
      return state;
    },
    async verifyEmailAddress(token) {
      const data = dataOf(await client.verifyPersonEmail({ body: { token } }), "verifyPersonEmail");
      if (data["email_verified"] !== true) {
        throw new ApiError(500, { error: { code: "internal_error", message: "verifyPersonEmail did not say the address is verified" } }, null);
      }
    },
    async confirmEmailChange(token) {
      const data = dataOf(await client.confirmMyEmailChange({ body: { token } }), "confirmMyEmailChange");
      const email = data["email"];
      if (typeof email !== "string") throw new ApiError(500, { error: { code: "internal_error", message: "email is not text" } }, null);
      return { email };
    },
  };
}
